import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, CheckCircle, Building, Users, CreditCard, Shield, TrendingUp, Zap } from 'lucide-react';

const Solutions = () => {
  const [activeTab, setActiveTab] = useState('oem');
  const location = useLocation();

  // Effect to handle URL hash navigation
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && ['oem', 'smb', 'financial', 'insurance'].includes(hash)) {
      setActiveTab(hash);
    }
  }, [location.hash]);

  const solutions = {
    oem: {
      title: 'OEM Dropship Enablement Revolution',
      subtitle: 'For Original Equipment Manufacturers',
      description: 'Transform your distribution strategy with autonomous agent-driven marketplace enablement',
      icon: Building,
      features: [
        {
          title: 'Autonomous OEM Marketplace Creation',
          description: 'AI agents automatically create industry-specific marketplaces for your product ecosystem',
          benefits: [
            'Intelligent Agent Network for automated marketplace creation',
            'Multi-Tier Distribution Orchestration from manufacturers to customers',
            'Dynamic Catalog Intelligence with real-time pricing',
            'Smart Channel Conflict Resolution while maximizing penetration'
          ]
        },
        {
          title: 'Advanced Dropship Enablement',
          description: 'Revolutionary catalog management that surpasses traditional platforms',
          benefits: [
            'Autonomous Product Onboarding of 1M+ SKUs in minutes vs. weeks',
            'Real-Time Inventory Synchronization across unlimited suppliers',
            'Dynamic Pricing Optimization based on market conditions',
            'Predictive Catalog Expansion using AI demand patterns'
          ]
        },
        {
          title: 'Intelligent Pricing & Inventory',
          description: 'Multi-tier pricing automation and channel-specific inventory allocation',
          benefits: [
            'Multi-Tier Pricing Automation for distributors, dealers, customers',
            'Channel-Specific Inventory Allocation optimization',
            'Dynamic Margin Management while remaining competitive',
            'Automated Contract Pricing for enterprise customers'
          ]
        }
      ],
      caseStudy: {
        title: 'Global OEM Manufacturing Giant',
        description: 'Global OEM with $10M+ revenue deploying CredFlexi for worldwide dropship enablement',
        challenge: 'Manual management of 50,000+ SKUs across 200+ distributors in 40 countries',
        results: [
          '300% increase in distributor onboarding speed',
          '60% reduction in pricing errors',
          '90% improvement in inventory accuracy',
          '$5M additional revenue in first year'
        ]
      }
    },
    smb: {
      title: 'SMB-to-Enterprise Marketplace Integration',
      subtitle: 'Connecting SMBs to Enterprise Players',
      description: 'Revolutionizing how small and medium businesses access enterprise markets',
      icon: Users,
      features: [
        {
          title: 'Autonomous SMB Onboarding',
          description: 'AI agents evaluate and qualify SMBs for enterprise partnerships in real-time',
          benefits: [
            'Instant Qualification with AI evaluation in real-time',
            'Automated Compliance Verification of certifications and licenses',
            'Intelligent Capability Matching across multiple industries',
            'Performance-Based Scaling with automatic privilege upgrades'
          ]
        },
        {
          title: 'Enterprise Integration Intelligence',
          description: 'Seamless system integration regardless of technology stack',
          benefits: [
            'Seamless System Integration with enterprise platforms',
            'Universal Data Translation between any formats (EDI, XML, JSON, CSV)',
            'Automated Training & Support based on technical sophistication',
            'Intelligent Partnership Development between SMBs and enterprises'
          ]
        },
        {
          title: 'Financial Enablement for SMBs',
          description: 'Instant credit assessment and performance-based credit increases',
          benefits: [
            'Instant Credit Assessment and financing access',
            'Performance-Based Credit Increases automatically',
            'Automated Invoice Factoring for improved cash flow',
            'Enterprise Payment Acceleration with faster payment terms'
          ]
        }
      ],
      caseStudy: {
        title: 'SMB Network Integration',
        description: 'Connecting 10,000+ SMBs to Fortune 500 enterprises',
        challenge: 'Manual onboarding and qualification processes taking 6+ months',
        results: [
          '95% reduction in onboarding time (6 months to 30 minutes)',
          '400% increase in qualified SMB partnerships',
          '80% improvement in compliance verification',
          '200% increase in enterprise-SMB transaction volume'
        ]
      }
    },
    financial: {
      title: 'Third-Party Finance Integration Revolution',
      subtitle: 'For Financial Institutions & Fintech Companies',
      description: 'Revolutionary integration beyond basic payment processing',
      icon: CreditCard,
      features: [
        {
          title: 'Intelligent Finance Agent Network',
          description: 'Autonomous credit orchestration and real-time risk assessment',
          benefits: [
            'Autonomous Credit Orchestration with automatic approvals',
            'Real-Time Risk Assessment continuously monitoring financial health',
            'Multi-Party Transaction Financing for complex structures',
            'Predictive Default Prevention with preventive measures'
          ]
        },
        {
          title: 'Advanced Invoice Module Integration',
          description: 'Complete financial ecosystem integration beyond traditional management',
          benefits: [
            'Smart Invoice Generation with optimal payment terms',
            'Dynamic Payment Optimization for cash flow',
            'Intelligent Factoring Engine with real-time opportunities',
            'Automated Dispute Resolution using AI-powered mediation'
          ]
        },
        {
          title: 'Supply Chain Finance Orchestration',
          description: 'Comprehensive financing across the entire supply chain',
          benefits: [
            'Purchase Order Financing across entire supply chain',
            'Inventory Financing based on real-time demand',
            'Working Capital Optimization for all parties',
            'Multi-Currency Management with automatic hedging'
          ]
        }
      ],
      caseStudy: {
        title: 'Top-10 Global Bank',
        description: 'Leading financial institution integrating supply chain finance automation',
        challenge: 'Manual processing of 100,000+ supply chain finance applications annually',
        results: [
          '95% reduction in application processing time',
          '70% increase in approval rates',
          '40% reduction in default rates',
          '$2M+ additional financing volume in first year'
        ]
      }
    },
    insurance: {
      title: 'Insurance Company Integration Excellence',
      subtitle: 'For Insurance Providers',
      description: 'Complete risk management and coverage orchestration through intelligent agents',
      icon: Shield,
      features: [
        {
          title: 'Intelligent Insurance Agent Network',
          description: 'Autonomous risk assessment and dynamic coverage optimization',
          benefits: [
            'Autonomous Risk Assessment pricing risks in real-time',
            'Dynamic Coverage Optimization based on changing risk profiles',
            'Multi-Party Policy Coordination involving multiple parties',
            'Predictive Claims Prevention with preventive measures'
          ]
        },
        {
          title: 'Advanced Risk Intelligence',
          description: 'Real-time risk monitoring and predictive modeling',
          benefits: [
            'Real-Time Risk Monitoring across entire commerce ecosystem',
            'Predictive Risk Modeling forecasting potential risks',
            'Automated Underwriting decisions in seconds rather than days',
            'Dynamic Premium Adjustment based on real-time assessment'
          ]
        },
        {
          title: 'Comprehensive Coverage Integration',
          description: 'Complete protection across all transaction types',
          benefits: [
            'Transaction Protection automatically applied for every transaction',
            'Supply Chain Risk Management monitoring disruptions',
            'Cyber Security Coverage with intelligent protection',
            'Performance Bond Management automatically managed'
          ]
        }
      ],
      caseStudy: {
        title: 'Enterprise Insurance Provider',
        description: 'Leading insurance company deploying automated risk management',
        challenge: 'Manual risk assessment and policy management across diverse industries',
        results: [
          '85% reduction in underwriting time',
          '50% improvement in risk assessment accuracy',
          '90% faster claims processing',
          '30% reduction in claims costs'
        ]
      }
    }
  };

  const tabs = [
    { id: 'oem', label: 'OEM Dropship', icon: Building },
    { id: 'smb', label: 'SMB Integration', icon: Users },
    { id: 'financial', label: 'Financial Services', icon: CreditCard },
    { id: 'insurance', label: 'Insurance', icon: Shield }
  ];

  const currentSolution = solutions[activeTab];
  const IconComponent = currentSolution.icon;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#854DFF] via-[#7A42E6] to-[#6F37CC] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Enterprise Solutions
          </h1>
          <p className="text-xl md:text-2xl text-[#B19CFF] mb-8 max-w-4xl mx-auto">
            Revolutionary agent-driven solutions that transform how enterprises operate, compete, and grow in the digital economy.
          </p>
        </div>
      </section>

      {/* Solution Tabs */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tab Navigation */}
          <div className="flex flex-wrap justify-center mb-16 border-b border-gray-200">
            {tabs.map((tab) => {
              const TabIcon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 mr-8 mb-4 transition-colors duration-200 ${
                    activeTab === tab.id
                      ? 'border-[#854DFF] text-[#854DFF]'
                      : 'border-transparent text-gray-500 hover:text-[#854DFF] hover:border-[#854DFF]/50'
                  }`}
                >
                  <TabIcon className="w-5 h-5 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Active Solution Content */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <div className="mx-auto w-20 h-20 bg-gradient-to-br from-purple-100 to-purple-200 rounded-2xl flex items-center justify-center mb-6">
                <IconComponent className="w-10 h-10 text-purple-600" />
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                {currentSolution.title}
              </h2>
              <p className="text-xl text-purple-600 font-semibold mb-4">
                {currentSolution.subtitle}
              </p>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                {currentSolution.description}
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid lg:grid-cols-3 gap-8 mb-16">
              {currentSolution.features.map((feature, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-lg transition-shadow duration-300">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                  <p className="text-gray-600 mb-6">{feature.description}</p>
                  <ul className="space-y-3">
                    {feature.benefits.map((benefit, benefitIndex) => (
                      <li key={benefitIndex} className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Case Study Section */}
      <section className="py-16 bg-gradient-to-br from-purple-50 to-purple-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  Case Study: {currentSolution.caseStudy.title}
                </h3>
                <p className="text-lg text-gray-600 mb-6">
                  {currentSolution.caseStudy.description}
                </p>
                <div className="mb-8">
                  <h4 className="font-semibold text-gray-900 mb-2">Challenge:</h4>
                  <p className="text-gray-600">{currentSolution.caseStudy.challenge}</p>
                </div>
                <Link
                  to="/contact"
                  className="inline-flex items-center bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-purple-800 transition-all duration-200"
                >
                  Get Similar Results
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </div>
              
              <div>
                <h4 className="text-2xl font-bold text-gray-900 mb-6">Results Achieved:</h4>
                <div className="grid grid-cols-2 gap-4">
                  {currentSolution.caseStudy.results.map((result, index) => (
                    <div key={index} className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl">
                      <div className="text-2xl font-bold text-purple-600 mb-2">
                        {result.split(' ')[0]}
                      </div>
                      <div className="text-sm text-gray-700">
                        {result.split(' ').slice(1).join(' ')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Competitive Advantages */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Beyond Traditional Platforms
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Revolutionary capabilities that traditional marketplace solutions simply cannot provide.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-green-600 mb-6 flex items-center">
                <CheckCircle className="w-8 h-8 mr-3" />
                What CredFlexi Provides
              </h3>
              <div className="space-y-4">
                {[
                  'Agent-Driven Intelligence: Autonomous decision making without human intervention',
                  'Multi-Party Orchestration: Manage unlimited parties in single transactions',
                  'Predictive Optimization: 98% accurate forecasting and automatic optimization',
                  'Enterprise-Grade Financial Integration: Direct integration with global banking systems',
                  'Industry-Specific Capabilities: Purpose-built solutions with regulatory compliance',
                  'Real-Time Adaptation: Agents adapt to changing conditions instantly'
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-red-600 mb-6 flex items-center">
                <svg className="w-8 h-8 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                What Traditional Platforms Cannot
              </h3>
              <div className="space-y-4">
                {[
                  'Static Marketplace Models: Limited to basic buyer-seller connections',
                  'Manual Catalog Management: Requires weeks for product onboarding',
                  'Basic Payment Processing: No embedded banking or advanced financial services',
                  'Limited Integration: Cannot handle complex multi-party transactions',
                  'Generic Solutions: No industry-specific features or compliance',
                  'Manual Risk Management: Basic risk assessment without predictive capabilities'
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <svg className="w-5 h-5 text-red-500 mt-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-purple-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Revolutionize Your Operations?
          </h2>
          <p className="text-xl text-purple-100 mb-8 max-w-3xl mx-auto">
            Discover how CredFlexi can transform your enterprise operations and unlock unprecedented growth opportunities.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-purple-600 hover:bg-purple-50 font-semibold text-lg px-8 py-4 rounded-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center"
            >
              Schedule Enterprise Demo
              <ArrowRight className="ml-3 h-5 w-5" />
            </Link>
            <Link
              to="/products"
              className="border-2 border-white/30 text-white hover:bg-white/10 font-semibold text-lg px-8 py-4 rounded-lg transition-all duration-300 inline-flex items-center justify-center backdrop-blur-sm"
            >
              Explore Products
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Solutions;
